import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { TopActivity } from "../../../types/insightDashboard";
import { formatNumber, getTopListItemStyle } from "../utils";
import { CardNotes } from "../CardNotes";

interface TopListProps {
  focusData: TopActivity[];
  comparisonData: TopActivity[];
  focusYear: number | string;
  comparisonYear: number | string;
  // What each period is called when that isn't its year, like "Past 6 Months"
  focusLabel?: string;
  comparisonLabel?: string;
  title: string;
  description?: string;
  notes?: string[];
  keyToDisplayNameFunction?: (key: string) => string;
  descriptionTerm?: string;
  itemLabel?: string;
}

export function TopList({
  focusData,
  comparisonData,
  focusYear,
  comparisonYear,
  focusLabel = String(focusYear),
  comparisonLabel = String(comparisonYear),
  title,
  description,
  notes,
  keyToDisplayNameFunction,
  descriptionTerm = "items",
  itemLabel = "Item",
}: TopListProps) {
  const maxListItem = 5;

  const createDisplayList = (data: TopActivity[], listType: string) => {
    const list: Array<{ key: string; displayName: string; value: number | string }> = data
      .sort((a, b) => b.value - a.value)
      .slice(0, maxListItem)
      .map((item) => ({
        key: item.key,
        displayName:
          item.displayName ||
          (keyToDisplayNameFunction ? keyToDisplayNameFunction(item.key) : item.key),
        value: formatNumber(item.value),
      }));

    while (list.length < maxListItem) {
      list.push({
        key: `empty-${listType}-${list.length}`,
        displayName: "—",
        value: "-",
      });
    }
    return list;
  };

  const focusList = createDisplayList(focusData, "focus");
  const comparisonList = createDisplayList(comparisonData, "comp");

  return (
    <Card className="tw:h-full">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>
          {description || `Top ${descriptionTerm} for ${focusLabel} vs ${comparisonLabel}`}
        </CardDescription>
        <CardNotes notes={notes} />
      </CardHeader>
      <CardContent>
        <div className="tw:grid tw:grid-cols-2 tw:gap-8">
          {/* Focus Year List */}
          <div>
            <h4 className="tw:font-bold tw:text-md tw:mb-2">Top in {focusLabel}</h4>
            <div className="tw:grid tw:grid-cols-2 tw:gap-2 tw:font-bold tw:text-sm tw:mb-2">
              <div>{itemLabel}</div>
              <div className="tw:text-right">Count</div>
            </div>
            <ul className="tw:space-y-1 tw:pl-0">
              {focusList.map((item, index) => (
                <li
                  key={item.key}
                  className="tw:grid tw:grid-cols-2 tw:gap-2 tw:items-center"
                  style={getTopListItemStyle(index, "focus")}
                >
                  <span className="tw:text-sm tw:font-medium tw:truncate">{item.displayName}</span>
                  <span className="tw:text-sm tw:text-right">{item.value}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Comparison Year List */}
          <div>
            <h4 className="tw:font-bold tw:text-md tw:mb-2">Top in {comparisonLabel}</h4>
            <div className="tw:grid tw:grid-cols-2 tw:gap-2 tw:font-bold tw:text-sm tw:mb-2">
              <div>{itemLabel}</div>
              <div className="tw:text-right">Count</div>
            </div>
            <ul className="tw:space-y-1 tw:pl-0">
              {comparisonList.map((item, index) => (
                <li
                  key={item.key}
                  className="tw:grid tw:grid-cols-2 tw:gap-2 tw:items-center"
                  style={getTopListItemStyle(index, "comparison")}
                >
                  <span className="tw:text-sm tw:font-medium tw:truncate">{item.displayName}</span>
                  <span className="tw:text-sm tw:text-right">{item.value}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
